// Formatierer (DESIGN §4.8): immer über Intl, Locale Standard en-GB (ANNAHME), „heute“ = APP_TODAY.
// `configureFormat()` setzt Locale und heutiges Datum einmal beim Plattform-Start.

const NARROW_NBSP = '\u202f';
const MINUS = '\u2212';
const DAY_MS = 86_400_000;

interface FormatConfig {
  locale: string;
  /** „Heute“ als ISO-Datum (APP_TODAY), sonst die Systemzeit. */
  today?: string;
}

const config: FormatConfig = { locale: 'en-GB' };

export function configureFormat(next: Partial<FormatConfig>): void {
  Object.assign(config, next);
}

/** Heutiges Datum (Mitternacht UTC) nach APP_TODAY bzw. der Systemzeit. */
export function today(): Date {
  if (config.today) return new Date(`${config.today}T00:00:00Z`);
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

type DateInput = Date | string | number;
const toDate = (value: DateInput): Date =>
  value instanceof Date
    ? value
    : new Date(typeof value === 'string' && value.length === 10 ? `${value}T00:00:00Z` : value);

export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}): string {
  return new Intl.NumberFormat(config.locale, options).format(value);
}

/**
 * Prozent aus einem Anteil (0.264 → „26.4 %“) mit schmalem Leerzeichen.
 * `digits`: Nachkommastellen (Bonus 1, Fortschritt 0).
 */
export function formatPercent(fraction: number, digits = 0): string {
  const number = formatNumber(fraction * 100, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${number}${NARROW_NBSP}%`;
}

/** Delta mit Vorzeichen und echtem Minus: „+4 pts“, „−1“. */
export function formatDelta(value: number, unit = '', digits = 0): string {
  const abs = formatNumber(Math.abs(value), { maximumFractionDigits: digits });
  const sign = value > 0 ? '+' : value < 0 ? MINUS : '';
  return `${sign}${abs}${unit ? ` ${unit}` : ''}`;
}

/** Multiplikator mit zwei Stellen: „1.43×“. */
export function formatMultiplier(value: number): string {
  return `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
}

/**
 * Geld aus Minor Units (Cent): „$125,000“, „€24,692“; unter 1.000 mit zwei Stellen.
 * `compact` für Diagramme und Kacheln („$125k“).
 */
export function formatMoney(
  minor: number,
  currency: string,
  options: { compact?: boolean } = {},
): string {
  const major = minor / 100;
  if (options.compact) {
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })
      .format(major)
      .replace('K', 'k');
  }
  const whole = Math.abs(major) >= 1000;
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(major);
}

export type DateStyle = 'short' | 'long' | 'time' | 'datetime' | 'month' | 'quarter';

/**
 * Datum: `short` „25 Oct“ (mit Jahr außerhalb des laufenden Jahres „9 Nov 2025“),
 * `long` „Sunday, 25 October 2026“, `time` „14:32“, `datetime` „25 Oct, 14:32“,
 * `month` „October 2026“, `quarter` „Q3 2026“.
 */
export function formatDate(value: DateInput, style: DateStyle = 'short'): string {
  const date = toDate(value);
  // Reine Kalenderdaten („2026-10-25“) in UTC, Zeitpunkte in der Zeitzone des Browsers.
  const tz = typeof value === 'string' && value.length === 10 ? { timeZone: 'UTC' } : {};
  const fmt = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(config.locale, { ...tz, ...options }).format(date);
  switch (style) {
    case 'long':
      return `${fmt({ weekday: 'long' })}, ${fmt({ day: 'numeric', month: 'long', year: 'numeric' })}`;
    case 'time':
      return fmt({ hour: '2-digit', minute: '2-digit', hour12: false });
    case 'datetime':
      return `${formatDate(value, 'short')}, ${formatDate(value, 'time')}`;
    case 'month':
      return fmt({ month: 'long', year: 'numeric' });
    case 'quarter': {
      const [year, month] = fmt({ year: 'numeric', month: 'numeric' }).split('/').reverse();
      return `Q${Math.floor((Number(month) - 1) / 3) + 1} ${year}`;
    }
    default: {
      const sameYear = fmt({ year: 'numeric' }) === String(today().getUTCFullYear());
      return fmt({ day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
    }
  }
}

/** Ganze Kalendertage von heute bis `value` (negativ = Vergangenheit). */
export function daysFromToday(value: DateInput): number {
  const date = toDate(value);
  const day = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return Math.round((day - today().getTime()) / DAY_MS);
}

/** Relativ nur bis 7 Tage („2 days ago“, „in 3 days“, „Today“), danach absolut. */
export function formatRelative(value: DateInput): string {
  const days = daysFromToday(value);
  if (Math.abs(days) > 7) return formatDate(value, 'short');
  if (days === 0) return 'Today';
  if (days === -1) return 'Yesterday';
  if (days === 1) return 'Tomorrow';
  return days < 0 ? `${-days} days ago` : `in ${days} days`;
}

export interface DurationParts {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
}

const LONG: Record<keyof DurationParts, [string, string]> = {
  years: ['year', 'years'],
  months: ['month', 'months'],
  weeks: ['week', 'weeks'],
  days: ['day', 'days'],
  hours: ['hour', 'hours'],
  minutes: ['min', 'min'],
};
const SHORT: Record<keyof DurationParts, string> = {
  years: 'yr',
  months: 'mo',
  weeks: 'wk',
  days: 'd',
  hours: 'h',
  minutes: 'min',
};

/** Dauer: ein Teil lang („6 weeks“, „3 months“, „14 min“), mehrere kurz („1 yr 8 mo“). */
export function formatDuration(parts: DurationParts): string {
  const entries = (Object.keys(LONG) as (keyof DurationParts)[])
    .map((unit) => [unit, parts[unit] ?? 0] as const)
    .filter(([, n]) => n > 0);
  if (entries.length === 0) return '0 min';
  if (entries.length === 1) {
    const [unit, n] = entries[0]!;
    return `${formatNumber(n)} ${LONG[unit][n === 1 ? 0 : 1]}`;
  }
  return entries.map(([unit, n]) => `${formatNumber(n)} ${SHORT[unit]}`).join(' ');
}
